import React, { useState, useEffect } from 'react';
import { handleNumericInput, handleNumericKeyDown, processNumericInput } from '../utils/inputUtils';
import { Package, Plus, Edit2, Trash2, Calendar, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { NativeSelect } from './ui/native-select';
import axios from 'axios';

// URL centralizado que funciona em produção mesmo com .env errado
import { getBackendUrl } from '../utils/config';
const API_URL = getBackendUrl();

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const StocksTab = () => {
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingStock, setEditingStock] = useState(null);
  const [stockExistente, setStockExistente] = useState(null);
  
  // Verificar se é admin
  const userStr = localStorage.getItem('user');
  const currentUser = userStr ? JSON.parse(userStr) : null;
  const isAdmin = currentUser?.role === 'admin';
  
  // Helper para selecionar todo o texto quando foca num input numérico
  const handleFocusSelect = (e) => {
    e.target.select();
    // Se o valor é 0, limpar o campo para facilitar a escrita
    if (e.target.value === '0' || e.target.value === '0.00') {
      setTimeout(() => {
        e.target.value = '';
        e.target.dispatchEvent(new Event('input', { bubbles: true }));
      }, 0);
    }
  };
  
  // Helper para converter input numérico (permite string vazia temporariamente)
  const parseNumberInput = (value) => {
    if (value === '' || value === null || value === undefined) return '';
    const num = parseFloat(value);
    return isNaN(num) ? 0 : num;
  };
  
  const [formData, setFormData] = useState({
    data: new Date().toISOString().split('T')[0],
    turno: 'Manhã',
    raspadinha_1: 0,
    raspadinha_2: 0,
    raspadinha_3: 0,
    raspadinha_5: 0,
    raspadinha_10: 0,
    raspadinha_15: 0,
    observacoes: ''
  });

  useEffect(() => {
    loadStocks();
  }, []);
  
  // Recarregar stocks quando são modificados (por revendas, transferências, etc.)
  useEffect(() => {
    const handleStockModificado = () => {
      loadStocks();
    };
    
    window.addEventListener('stockModificado', handleStockModificado);
    
    return () => {
      window.removeEventListener('stockModificado', handleStockModificado);
    };
  }, []);

  // Carregar stock existente quando modal abre
  useEffect(() => {
    if (isModalOpen && !editingStock) {
      loadStockExistente();
    }
  }, [isModalOpen, editingStock, formData.turno]);

  const loadStocks = async () => {
    try {
      setLoading(true);
      setError(null); // Limpar erros anteriores
      const token = localStorage.getItem('token');
      // Buscar apenas os stocks ativos (sem filtros)
      const response = await axios.get(`${API_URL}/api/stocks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Ordenar: Manhã primeiro, depois Tarde
      const stocksOrdenados = response.data.sort((a, b) => {
        if (a.turno === 'Manhã' && b.turno === 'Tarde') return -1;
        if (a.turno === 'Tarde' && b.turno === 'Manhã') return 1;
        return 0;
      });
      
      setStocks(stocksOrdenados);
    } catch (err) {
      const errorMsg = 'Erro ao carregar stocks';
      setError(errorMsg);
      console.error('Error loading stocks:', err);
      
      // Limpar erro automaticamente após 5 segundos
      setTimeout(() => setError(null), 5000);
    } finally {
      setLoading(false);
    }
  };

  const loadStockExistente = async () => {
    try {
      const token = localStorage.getItem('token');
      // Buscar stocks atuais (is_current=true) - sempre mostra o último disponível
      const response = await axios.get(`${API_URL}/api/stocks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Pegar o stock do turno correspondente (Manhã ou Tarde)
      if (response.data && response.data.length > 0) {
        const stockTurno = response.data.find(s => s.turno === formData.turno);
        if (stockTurno) {
          setStockExistente(stockTurno);
        } else {
          // Se não houver do turno específico, pegar o primeiro disponível
          setStockExistente(response.data[0]);
        }
      }
    } catch (err) {
      console.log('Nenhum stock existente encontrado');
      setStockExistente(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      // Limpar erro anterior
      setError(null);
      
      if (editingStock) {
        // Update
        await axios.put(
          `${API_URL}/api/stocks/${editingStock.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // Create
        await axios.post(
          `${API_URL}/api/stocks`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      setIsModalOpen(false);
      resetForm();
      loadStocks();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Erro ao salvar stock';
      setError(errorMsg);
      console.error('Error saving stock:', err);
      
      // Limpar erro automaticamente após 5 segundos
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleEdit = (stock) => {
    setEditingStock(stock);
    setFormData({
      data: stock.data,
      turno: stock.turno,
      raspadinha_1: stock.raspadinha_1,
      raspadinha_2: stock.raspadinha_2,
      raspadinha_3: stock.raspadinha_3,
      raspadinha_5: stock.raspadinha_5,
      raspadinha_10: stock.raspadinha_10,
      raspadinha_15: stock.raspadinha_15,
      observacoes: stock.observacoes || ''
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (stockId) => {
    if (!window.confirm('Tem certeza que deseja eliminar este stock?')) return;
    
    try {
      setError(null); // Limpar erros anteriores
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/stocks/${stockId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadStocks();
    } catch (err) {
      const errorMsg = 'Erro ao eliminar stock';
      setError(errorMsg);
      console.error('Error deleting stock:', err);
      
      // Limpar erro automaticamente após 5 segundos
      setTimeout(() => setError(null), 5000);
    }
  };

  const resetForm = () => {
    setEditingStock(null);
    setStockExistente(null);
    const hoje = new Date().toISOString().split('T')[0];
    setFormData({
      data: hoje,
      turno: 'Manhã',
      raspadinha_1: 0,
      raspadinha_2: 0,
      raspadinha_3: 0,
      raspadinha_5: 0,
      raspadinha_10: 0,
      raspadinha_15: 0,
      observacoes: ''
    });
  };

  const getTotalRaspadinhas = (stock) => {
    return stock.raspadinha_1 + stock.raspadinha_2 + stock.raspadinha_3 + 
           stock.raspadinha_5 + stock.raspadinha_10 + stock.raspadinha_15;
  };

  const getValorTotal = (stock) => {
    return (
      stock.raspadinha_1 * 1 + 
      stock.raspadinha_2 * 2 + 
      stock.raspadinha_3 * 3 + 
      stock.raspadinha_5 * 5 + 
      stock.raspadinha_10 * 10 + 
      stock.raspadinha_15 * 15
    );
  };

  // Função para recalcular stocks baseado nos últimos fechos
  const [recalculando, setRecalculando] = useState(false);
  
  const recalcularStocks = async () => {
    try {
      setRecalculando(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/stocks/sincronizar-fecho`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const detalhes = (response.data.resultados || []).map(r => {
          const origem = r.data_origem_valores ? `origem ${r.data_origem_valores}` : r.status;
          const totais = `r1=${r.raspadinha_1} r2=${r.raspadinha_2} r3=${r.raspadinha_3} r5=${r.raspadinha_5} r10=${r.raspadinha_10} r15=${r.raspadinha_15}`;
          return `${r.turno} (${r.data_hoje}, ${origem}): ${totais}`;
        }).join('\n');
        alert(`✅ ${response.data.message}\n\n${detalhes}`);
        loadStocks();
      }
    } catch (err) {
      alert('❌ Erro ao recalcular stocks: ' + (err.response?.data?.detail || err.message));
      console.error('Error recalculating stocks:', err);
    } finally {
      setRecalculando(false);
    }
  };

  if (loading) return <div className="text-center py-8">Carregando...</div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-gradient-card border-primary/20">
        <CardContent className="p-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <Package className="h-5 w-5 text-primary" />
              <span className="font-semibold">Stocks Atuais (Manhã e Tarde)</span>
            </div>
            
            {isAdmin && (
              <div className="flex gap-2 flex-wrap">
                <Button 
                  onClick={recalcularStocks}
                  disabled={recalculando}
                  variant="outline"
                  className="border-orange-500 text-orange-600 hover:bg-orange-50"
                >
                  {recalculando ? '⏳ A recalcular...' : '🔄 Recalcular dos Fechos'}
                </Button>
                <Button 
                  onClick={() => { resetForm(); setIsModalOpen(true); }} 
                  className="bg-primary hover:bg-primary/90"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Adicionar Stock
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Modal customizado */}
      {isModalOpen && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        >
          <div 
            className="bg-card rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto relative z-50"
          >
            <div className="p-6 border-b border-border">
              <h2 className="text-xl font-semibold">
                {editingStock ? 'Editar Stock' : 'Adicionar Stock'}
              </h2>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Turno</label>
                      <NativeSelect
                        value={formData.turno}
                        onChange={(e) => setFormData({...formData, turno: e.target.value})}
                        className="w-full"
                        required
                        disabled={editingStock !== null}
                      >
                        <option value="Manhã">Manhã</option>
                        <option value="Tarde">Tarde</option>
                      </NativeSelect>
                    </div>
                  </div>

                  {/* Stock Existente */}
                  {!editingStock && stockExistente && (
                    <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Package className="h-5 w-5 text-blue-600" />
                        <h3 className="font-semibold text-blue-800">
                          Stock Atual {stockExistente.turno} 
                          {stockExistente.data && ` (${new Date(stockExistente.data).toLocaleDateString('pt-PT')})`}
                        </h3>
                      </div>
                      <div className="grid grid-cols-3 gap-3 text-sm">
                        <div className="bg-white rounded p-2">
                          <p className="text-xs text-muted-foreground">Raspadinhas 1€</p>
                          <p className="font-bold text-lg text-blue-600">{stockExistente.raspadinha_1}</p>
                        </div>
                        <div className="bg-white rounded p-2">
                          <p className="text-xs text-muted-foreground">Raspadinhas 2€</p>
                          <p className="font-bold text-lg text-blue-600">{stockExistente.raspadinha_2}</p>
                        </div>
                        <div className="bg-white rounded p-2">
                          <p className="text-xs text-muted-foreground">Raspadinhas 3€</p>
                          <p className="font-bold text-lg text-blue-600">{stockExistente.raspadinha_3}</p>
                        </div>
                        <div className="bg-white rounded p-2">
                          <p className="text-xs text-muted-foreground">Raspadinhas 5€</p>
                          <p className="font-bold text-lg text-blue-600">{stockExistente.raspadinha_5}</p>
                        </div>
                        <div className="bg-white rounded p-2">
                          <p className="text-xs text-muted-foreground">Raspadinhas 10€</p>
                          <p className="font-bold text-lg text-blue-600">{stockExistente.raspadinha_10}</p>
                        </div>
                        <div className="bg-white rounded p-2">
                          <p className="text-xs text-muted-foreground">Raspadinhas 15€</p>
                          <p className="font-bold text-lg text-blue-600">{stockExistente.raspadinha_15}</p>
                        </div>
                      </div>
                      <p className="text-xs text-blue-700 mt-2 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        Este é o stock disponível atualmente no sistema
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Raspadinhas 1€</label>
                      <input
                        type="text" inputMode="decimal"
                        min="0"
                        value={formData.raspadinha_1}
                        onChange={(e) => setFormData({...formData, raspadinha_1: e.target.value === '' ? '' : parseInt(e.target.value) || 0})}
                        onFocus={handleFocusSelect}
                        onKeyDown={handleNumericKeyDown}
                        className="w-full px-3 py-2 border rounded-md bg-background"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Raspadinhas 2€</label>
                      <input
                        type="text" inputMode="decimal"
                        min="0"
                        value={formData.raspadinha_2}
                        onChange={(e) => setFormData({...formData, raspadinha_2: e.target.value === '' ? '' : parseInt(e.target.value) || 0})}
                        onFocus={handleFocusSelect}
                        onKeyDown={handleNumericKeyDown}
                        className="w-full px-3 py-2 border rounded-md bg-background"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Raspadinhas 3€</label>
                      <input
                        type="text" inputMode="decimal"
                        min="0"
                        value={formData.raspadinha_3}
                        onChange={(e) => setFormData({...formData, raspadinha_3: e.target.value === '' ? '' : parseInt(e.target.value) || 0})}
                        onFocus={handleFocusSelect}
                        onKeyDown={handleNumericKeyDown}
                        className="w-full px-3 py-2 border rounded-md bg-background"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Raspadinhas 5€</label>
                      <input
                        type="text" inputMode="decimal"
                        min="0"
                        value={formData.raspadinha_5}
                        onChange={(e) => setFormData({...formData, raspadinha_5: e.target.value === '' ? '' : parseInt(e.target.value) || 0})}
                        onFocus={handleFocusSelect}
                        onKeyDown={handleNumericKeyDown}
                        className="w-full px-3 py-2 border rounded-md bg-background"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Raspadinhas 10€</label>
                      <input
                        type="text" inputMode="decimal"
                        min="0"
                        value={formData.raspadinha_10}
                        onChange={(e) => setFormData({...formData, raspadinha_10: e.target.value === '' ? '' : parseInt(e.target.value) || 0})}
                        onFocus={handleFocusSelect}
                        onKeyDown={handleNumericKeyDown}
                        className="w-full px-3 py-2 border rounded-md bg-background"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Raspadinhas 15€</label>
                      <input
                        type="text" inputMode="decimal"
                        min="0"
                        value={formData.raspadinha_15}
                        onChange={(e) => setFormData({...formData, raspadinha_15: e.target.value === '' ? '' : parseInt(e.target.value) || 0})}
                        onFocus={handleFocusSelect}
                        onKeyDown={handleNumericKeyDown}
                        className="w-full px-3 py-2 border rounded-md bg-background"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Observações</label>
                    <textarea
                      value={formData.observacoes}
                      onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                      className="w-full px-3 py-2 border rounded-md bg-background"
                      rows={3}
                    />
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => { setIsModalOpen(false); resetForm(); }}
                    >
                      Cancelar
                    </Button>
                    <Button type="submit" className="bg-primary hover:bg-primary/90">
                      {editingStock ? 'Atualizar' : 'Adicionar'}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          )}

      {error && (
        <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded relative">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Lista de Stocks */}
      <div className="space-y-4">
        {stocks.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>
                {isAdmin 
                  ? 'Nenhum stock registrado. Clique em "Adicionar Stock" para criar.' 
                  : 'Nenhum stock disponível no momento.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          stocks.map((stock) => (
            <Card key={stock.id} className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Package className="h-5 w-5 text-primary" />
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2 flex-wrap">
                        Stock {stock.turno}
                        {stock.data && (
                          <span className="text-xs font-normal text-muted-foreground bg-muted/50 px-2 py-0.5 rounded">
                            {new Date(stock.data).toLocaleDateString('pt-PT')}
                          </span>
                        )}
                        {stock.data === new Date().toISOString().split('T')[0] && (
                          <span className="text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded">
                            HOJE
                          </span>
                        )}
                      </CardTitle>
                      {stock.observacoes && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {stock.observacoes}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {isAdmin && (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(stock)}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(stock.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  <div className="p-3 bg-gradient-card rounded-lg border border-border">
                    <p className="text-xs text-muted-foreground mb-1">1€</p>
                    <p className="text-2xl font-bold text-primary">{stock.raspadinha_1}</p>
                  </div>
                  <div className="p-3 bg-gradient-card rounded-lg border border-border">
                    <p className="text-xs text-muted-foreground mb-1">2€</p>
                    <p className="text-2xl font-bold text-primary">{stock.raspadinha_2}</p>
                  </div>
                  <div className="p-3 bg-gradient-card rounded-lg border border-border">
                    <p className="text-xs text-muted-foreground mb-1">3€</p>
                    <p className="text-2xl font-bold text-primary">{stock.raspadinha_3}</p>
                  </div>
                  <div className="p-3 bg-gradient-card rounded-lg border border-border">
                    <p className="text-xs text-muted-foreground mb-1">5€</p>
                    <p className="text-2xl font-bold text-primary">{stock.raspadinha_5}</p>
                  </div>
                  <div className="p-3 bg-gradient-card rounded-lg border border-border">
                    <p className="text-xs text-muted-foreground mb-1">10€</p>
                    <p className="text-2xl font-bold text-primary">{stock.raspadinha_10}</p>
                  </div>
                  <div className="p-3 bg-gradient-card rounded-lg border border-border">
                    <p className="text-xs text-muted-foreground mb-1">15€</p>
                    <p className="text-2xl font-bold text-primary">{stock.raspadinha_15}</p>
                  </div>
                </div>
                
                <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total de Raspadinhas</p>
                    <p className="text-xl font-bold">{getTotalRaspadinhas(stock)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Valor Total</p>
                    <p className="text-xl font-bold text-success">€{getValorTotal(stock)}</p>
                  </div>
                </div>
                
                {stock.observacoes && (
                  <div className="mt-3 p-3 bg-muted/30 rounded">
                    <p className="text-sm text-muted-foreground mb-1">Observações:</p>
                    <p className="text-sm">{stock.observacoes}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default StocksTab;
